import React, { useState } from 'react';
import { getThemeColors } from '../libs/common/themeConfig';
import { isCSSite } from '../libs/common/siteConfig';
import { useTranslation } from '../libs/common/i18n';
import {
  BookOpenIcon,
  ChatBubbleBottomCenterIcon,
  CurrencyDollarIcon,
  NewspaperIcon,
  CogIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

/**
 * Get About links based on site type
 */
export const getAboutLinks = () => {
  const isCS = isCSSite();
  // Note: This function is called outside component, so we can't use useTranslation here
  // Translations will be applied in the component
  return [
    {
      titleKey: 'majorRulebook',
      subtitleKey: isCS ? 'majorRulebookSubtitleCS' : 'majorRulebookSubtitleR6',
      icon: BookOpenIcon,
      path: isCS
        ? "https://counter-strike.net/csgo_major_supplemental_rulebook/#Final-Rankings-Major"
        : "https://www.ubisoft.com/en-us/esports/rainbow-six/siege/rules",
      external: true,
    },
    {
      titleKey: 'discord',
      subtitleKey: 'discordSubtitle',
      icon: ChatBubbleBottomCenterIcon,
      path: "https://discord.gg/KYNbRYrZGe",
      external: true,
    },
    {
      titleKey: 'kofi',
      subtitleKey: 'kofiSubtitle',
      icon: CurrencyDollarIcon,
      path: "https://ko-fi.com/ieb233",
      external: true,
    },
    {
      titleKey: 'tutorial',
      subtitleKey: 'tutorialSubtitle',
      icon: NewspaperIcon,
      path: "https://iebb.medium.com/how-to-use-the-matchup-site-in-2022-18366c9e60da",
      external: true,
    },
    {
      titleKey: 'steamProfile',
      subtitleKey: 'steamProfileSubtitle',
      icon: CogIcon,
      path: "https://steamcommunity.com/id/iebbbb",
      external: true,
    },
  ];
};

/**
 * About menu dropdown for global navigation
 */
export const AboutMenu = () => {
  const themeColors = getThemeColors();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const aboutLinks = getAboutLinks();

  const handleClick = (path, external) => {
    if (external) {
      window.open(path, '_blank');
    } else {
      window.location.href = path;
    }
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded transition-colors min-h-[2.5rem] min-w-[2.5rem] touch-manipulation flex items-center justify-center hover:bg-[var(--hover-bg)]"
        style={{
          color: themeColors.text,
          backgroundColor: 'transparent',
          border: `1px solid ${themeColors.border}`,
          '--hover-bg': themeColors.hover,
        }}
        title={t('about')}
      >
        <InformationCircleIcon style={{ width: '1.125rem', height: '1.125rem' }} />
      </button>

      {isOpen && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 49 }}
            onClick={() => setIsOpen(false)}
          />
          <div
            style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '0.5rem',
              minWidth: '14rem',
              maxWidth: '18rem',
              backgroundColor: themeColors.surface,
              border: `1px solid ${themeColors.border}`,
              borderRadius: '0.25rem',
              zIndex: 60,
            }}
          >
            {aboutLinks.map((link) => {
              const Icon = link.icon;
              return (
                <button
                  key={link.path}
                  onClick={() => handleClick(link.path, link.external)}
                  className="w-full py-3.5 px-4 text-left transition-colors flex items-center gap-3 border-b min-h-[3.5rem] touch-manipulation hover:bg-[var(--hover-bg)]"
                  style={{
                    backgroundColor: 'transparent',
                    borderBottomColor: themeColors.border,
                    '--hover-bg': themeColors.hover,
                  }}
                >
                  <Icon style={{ width: '1.25rem', height: '1.25rem', color: themeColors.textSecondary, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: themeColors.text,
                      }}
                    >
                      {t(link.titleKey)}
                    </div>
                    <div
                      style={{
                        fontSize: '0.75rem',
                        color: themeColors.textSecondary,
                        marginTop: '0.125rem',
                      }}
                    >
                      {t(link.subtitleKey)}
                    </div>
                  </div>
                  {link.external && (
                    <svg
                      style={{ width: '0.875rem', height: '0.875rem', color: themeColors.textSecondary, flexShrink: 0 }}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};
